import { CommonLayoutModule } from '@aiwyn/common/layout';
import { LoadableStatus, SharedModule, TimeAgoPipe } from '@aiwyn/shared';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Store } from '@ngxs/store';
import { HighlightPipe } from '../../../../shared/pipes/highlight.pipe';
import {
  EngagementListItem,
  EngagementListPaymentTypeColor,
  EngagementListPaymentTypeDisplay,
  EngagementListPrepaymentStatusColor,
  EngagementListPrepaymentStatusDisplay,
  EngagementListStatusColor,
  EngagementListStatusDisplay,
} from '../../../../api/models';
import { EngagementsState } from '../../state';

export enum EngagementColumn {
  ACCOUNT = 'clientName',
  ENGAGEMENT = 'name',
  PARTNER = 'partnerName',
  START_DATE = 'startDate',
  EXECUTED_DATE = 'executedDate',
  STATUS = 'status',
  SENT_DATE = 'sentAt',
  PAYMENT_TYPE = 'paymentType',
}

export interface PaginationOptions {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions: number[];
}

@Component({
  selector: 'app-engagements-table',
  templateUrl: './engagements-table.component.html',
  styleUrls: ['./engagements-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CommonLayoutModule,
    SharedModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinner,
    HighlightPipe,
    TimeAgoPipe,
  ],
})
export class EngagementsTableComponent implements AfterViewInit {
  private readonly store = inject(Store);

  readonly dataSource = input.required<MatTableDataSource<EngagementListItem>>();
  readonly displayedColumns = input.required<string[]>();
  readonly highlightTerm = input<string>('');
  readonly listStatus = input.required<LoadableStatus>();
  readonly paginationOptions = input.required<PaginationOptions>();
  readonly searchControl = input.required<UntypedFormControl>();
  readonly sortActive = input<string>('');
  readonly sortDirection = input<SortDirection>('');

  readonly rowClick = output<EngagementListItem>();
  readonly pageChange = output<PageEvent>();
  readonly sortChange = output<Sort>();

  readonly LoadableStatus = LoadableStatus;
  readonly EngagementColumn = EngagementColumn;
  readonly EngagementListStatusDisplay = EngagementListStatusDisplay;
  readonly EngagementListStatusColor = EngagementListStatusColor;
  readonly EngagementListPaymentTypeDisplay = EngagementListPaymentTypeDisplay;
  readonly EngagementListPaymentTypeColor = EngagementListPaymentTypeColor;
  readonly EngagementListPrepaymentStatusDisplay = EngagementListPrepaymentStatusDisplay;
  readonly EngagementListPrepaymentStatusColor = EngagementListPrepaymentStatusColor;

  ngAfterViewInit(): void {
    this.initDataSource();
  }

  private initDataSource(): void {
    const dataSource = this.dataSource();
    if (dataSource) {
      dataSource.filterPredicate = (item, searchValue) => this.filterPredicate(item, searchValue);
      dataSource.filter = this.store.selectSnapshot(EngagementsState.getTableSearchValue);
    }
  }

  private filterPredicate(item: EngagementListItem, searchValue: string): boolean {
    const columns = this.displayedColumns();

    const columnValueMatches = Object.entries(item)
      .filter(([key]) => columns.includes(key as EngagementColumn))
      .some(([key, value]) => {
        if (key === EngagementColumn.STATUS) {
          const statusLabel = item.prepaymentStatus
            ? EngagementListPrepaymentStatusDisplay[item.prepaymentStatus]
            : EngagementListStatusDisplay[item.status];
          return statusLabel?.toLowerCase().includes(searchValue);
        }

        if (key === EngagementColumn.PAYMENT_TYPE) {
          return EngagementListPaymentTypeDisplay[value]?.toLowerCase().includes(searchValue);
        }

        return `${value}`.toLowerCase().includes(searchValue);
      });

    return columnValueMatches
      || (item.actionRequired && 'action required'.includes(searchValue))
      || (item.statusDate?.toLowerCase().includes(searchValue));
  }
}
